/**
 * BlueNote recording overlay — the floating pill that follows the author to
 * EVERY route (portal to body; provider lives above the router).
 *
 * States:
 *  - armed: share + mic acquired, nothing recording — Record (red) starts it,
 *    discard throws it away. Lets the author set the scene before frame one.
 *  - recording: timers + Mark, Pause (yellow), Stop (red), discard.
 *  - paused: frozen timers + Resume, Stop (red), discard.
 *  - stopped off-editor with clips: "finish your guide" chip.
 *
 * The pill is draggable by its grip — position persists across routes because
 * this component never unmounts (it lives beside the provider).
 * Discard is two-step (first press arms a confirm for 3s) — no window.confirm,
 * which would freeze the page mid-capture.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Camera, Circle, EyeOff, GripVertical, Pause, Play, Square, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useHelpRecorder } from './HelpRecorderContext';

const fmt = (ms: number): string => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

export function RecorderOverlay() {
  const { t } = useTranslation('help');
  const recorder = useHelpRecorder();
  const navigate = useNavigate();
  const location = useLocation();
  // Local tick so only THIS component re-renders for the timer.
  const [, setTick] = useState(0);
  // null = default anchor (bottom-right); set once dragged.
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ pointerId: number; dx: number; dy: number } | null>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Minimized-to-dot: tab capture records the page, so the pill would appear
  // in the published video. Hidden = a ~10px translucent dot viewers won't
  // notice; marks still work via Tab/M/⏯/voice and Chrome's native
  // "Stop sharing" bar (never captured) still stops cleanly.
  const [minimized, setMinimized] = useState(false);

  const snap = recorder.getSnapshot();
  const armed = snap.phase === 'armed';
  const recording = snap.phase === 'recording';
  const paused = snap.phase === 'paused';
  const active = armed || recording || paused;
  const draftPending = snap.phase === 'stopped' && snap.segmentCount > 0 && location.pathname !== '/help/manage';

  useEffect(() => {
    if (!recording) return;
    const h = setInterval(() => setTick((v) => v + 1), 250);
    return () => clearInterval(h);
  }, [recording]);

  useEffect(() => {
    if (!active) {
      setConfirmDiscard(false);
      setMinimized(false);
    }
  }, [active]);

  const askDiscard = () => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    if (confirmDiscard) {
      setConfirmDiscard(false);
      recorder.discard();
      return;
    }
    setConfirmDiscard(true);
    confirmTimerRef.current = setTimeout(() => setConfirmDiscard(false), 3000);
  };

  const onDragStart = (e: React.PointerEvent) => {
    const rect = pillRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { pointerId: e.pointerId, dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    const rect = pillRef.current?.getBoundingClientRect();
    if (!drag || drag.pointerId !== e.pointerId || !rect) return;
    setPos({
      x: Math.min(Math.max(e.clientX - drag.dx, 4), window.innerWidth - rect.width - 4),
      y: Math.min(Math.max(e.clientY - drag.dy, 4), window.innerHeight - rect.height - 4),
    });
  };
  const onDragEnd = (e: React.PointerEvent) => {
    if (dragRef.current?.pointerId === e.pointerId) dragRef.current = null;
  };

  if (!active && !draftPending) return null;

  return createPortal(
    <div
      // Default bottom-24 right-4: clear of the sonner toast stack. Once
      // dragged, the author's position wins.
      className={cn('fixed z-[100] flex flex-col items-end gap-1.5', !pos && 'bottom-24 right-4')}
      style={pos ? { left: pos.x, top: pos.y } : undefined}
      role="region"
      aria-label={t('overlay.region')}
    >
      {active && minimized ? (
        <button
          type="button"
          onClick={() => setMinimized(false)}
          aria-label={t('overlay.restore')}
          title={t('overlay.restore')}
          className={cn(
            'h-2.5 w-2.5 cursor-pointer rounded-full opacity-40 transition-opacity hover:opacity-100',
            paused ? 'bg-amber-500' : 'bg-red-500',
          )}
        />
      ) : active ? (
        <>
          <div
            ref={pillRef}
            className="flex items-center gap-2 rounded-xl border bg-background/95 p-2 shadow-lg backdrop-blur"
          >
            <span
              className="cursor-grab touch-none text-muted-foreground/60 hover:text-foreground active:cursor-grabbing"
              aria-label={t('overlay.drag')}
              onPointerDown={onDragStart}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              onPointerCancel={onDragEnd}
            >
              <GripVertical className="h-4 w-4" aria-hidden="true" />
            </span>

            {armed ? (
              <>
                <span className="text-xs font-medium text-muted-foreground">{t('overlay.armed')}</span>
                <Button
                  size="sm"
                  className="gap-1.5 bg-red-600 text-white hover:bg-red-700"
                  onClick={() => recorder.begin()}
                >
                  <Circle className="h-3 w-3 fill-current" aria-hidden="true" />
                  {t('overlay.record')}
                </Button>
              </>
            ) : (
              <>
                <span
                  className={cn(
                    'h-2.5 w-2.5 rounded-full',
                    paused ? 'bg-amber-500' : 'animate-pulse bg-red-500 motion-reduce:animate-none',
                  )}
                  aria-hidden="true"
                />
                <span className="text-sm font-semibold tabular-nums text-foreground">{fmt(snap.fullElapsedMs)}</span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {t('overlay.clip', { n: snap.segmentCount + 1 })} · {fmt(snap.segElapsedMs)}
                </span>
                {recording && (
                  <Button size="sm" className="gap-1.5" onClick={() => recorder.mark()}>
                    <Camera className="h-4 w-4" aria-hidden="true" />
                    {t('overlay.markStep')}
                    <kbd className="rounded border border-primary-foreground/40 px-1 text-[10px] font-semibold">Tab</kbd>
                  </Button>
                )}
                {recording ? (
                  <Button
                    size="sm"
                    className="gap-1.5 bg-amber-400 text-amber-950 hover:bg-amber-500"
                    onClick={() => recorder.pause()}
                  >
                    <Pause className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('overlay.pause')}
                  </Button>
                ) : (
                  <Button size="sm" className="gap-1.5" onClick={() => recorder.resume()}>
                    <Play className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('overlay.resume')}
                  </Button>
                )}
                <Button
                  size="sm"
                  className="gap-1.5 bg-red-600 text-white hover:bg-red-700"
                  onClick={() => {
                    recorder.stop();
                    navigate('/help/manage');
                  }}
                >
                  <Square className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('overlay.stop')}
                </Button>
              </>
            )}

            {(recording || paused) && (
              <Button
                size="sm"
                variant="ghost"
                className="px-2"
                onClick={() => setMinimized(true)}
                aria-label={t('overlay.hide')}
                title={t('overlay.hideHint')}
              >
                <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            )}
            <Button
              size="sm"
              variant={confirmDiscard ? 'destructive' : 'ghost'}
              className="gap-1 px-2"
              onClick={askDiscard}
              aria-label={t('overlay.discard')}
              title={t('overlay.discard')}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              {confirmDiscard && <span className="text-xs">{t('overlay.discardConfirm')}</span>}
            </Button>
          </div>
          {(recording || paused) && (
            <p
              className="max-w-xs truncate rounded-lg border bg-background/90 px-2.5 py-1 text-xs text-muted-foreground shadow backdrop-blur"
              aria-live="polite"
            >
              {paused ? t('overlay.pausedHint') : snap.liveCaption || t('overlay.markHint')}
            </p>
          )}
        </>
      ) : (
        <Button size="sm" className="shadow-lg" onClick={() => navigate('/help/manage')}>
          {t('overlay.draftChip', { count: snap.segmentCount })}
        </Button>
      )}
    </div>,
    document.body,
  );
}
